import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listAdminTeam, grantAdminCapability, revokeAdminCapability } from "@/lib/admin-team.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/team")({
  head: () => ({ meta: [{ title: "Admin team — The Hospitality Reserve" }] }),
  component: AdminTeamPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const ROLE_LABELS: Record<string, string> = {
  admin: "Super-admin",
  admin_finance: "Finance",
  admin_safety: "Safety",
  admin_support: "Support",
};

function AdminTeamPage() {
  const load = useServerFn(listAdminTeam);
  const grant = useServerFn(grantAdminCapability);
  const revoke = useServerFn(revokeAdminCapability);
  const [team, setTeam] = useState<Array<{ user_id: string; full_name: string | null; email: string | null; roles: string[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin_finance" | "admin_safety" | "admin_support" | "admin">("admin_support");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const r = await load();
    if ("error" in r) toast.error(r.error);
    else setTeam(r.team);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const handleGrant = async () => {
    if (!email) return;
    setBusy(true);
    try {
      const r = await grant({ data: { email, role } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Capability granted.");
      setEmail("");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Could not grant."); }
    finally { setBusy(false); }
  };

  const handleRevoke = async (user_id: string, r: string) => {
    if (!confirm(`Revoke ${ROLE_LABELS[r] ?? r}?`)) return;
    setBusy(true);
    try {
      const res = await revoke({ data: { user_id, role: r as never } });
      if ("error" in res) throw new Error(res.error);
      toast.success("Revoked.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Could not revoke."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin</p>
        <h1 className="text-3xl font-light">Admin team & capabilities</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Grant specialized admin capabilities (Finance, Safety, Support) to existing users by email.
          Super-admins inherit every capability.
        </p>

        <section className="mt-6 border border-border p-6">
          <h2 className="text-lg font-light">Grant capability</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <div>
              <Label>User email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" />
            </div>
            <div>
              <Label>Capability</Label>
              <select className="block h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={role} onChange={(e) => setRole(e.target.value as never)}>
                <option value="admin_finance">Finance</option>
                <option value="admin_safety">Safety</option>
                <option value="admin_support">Support</option>
                <option value="admin">Super-admin</option>
              </select>
            </div>
            <Button onClick={handleGrant} disabled={busy || !email}>Grant</Button>
          </div>
        </section>

        <section className="mt-6 border border-border">
          <div className="border-b border-border p-4 text-xs tracking-wide-luxury text-muted-foreground">Current team</div>
          {loading ? <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          : team.length === 0 ? <p className="p-6 text-sm text-muted-foreground">No admins yet.</p>
          : (
            <ul className="divide-y divide-border">
              {team.map((m) => (
                <li key={m.user_id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className="text-sm">{m.full_name ?? "—"}</p>
                    <p className="text-xs text-muted-foreground">{m.email ?? m.user_id}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {m.roles.map((r) => (
                      <span key={r} className="inline-flex items-center gap-2 border border-border px-2 py-1 text-[11px] tracking-wide-luxury">
                        {ROLE_LABELS[r] ?? r}
                        <button type="button" onClick={() => handleRevoke(m.user_id, r)} disabled={busy} className="text-destructive hover:underline">×</button>
                      </span>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
